import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';

// Which offers run on this product, while it is being built (#5443).
//
// Two things this step has to be honest about, and both of them come from where the
// data actually lives:
//
//   1. An offer is attached to the PRICELIST (coupon_packages), not to the product.
//      Attaching one here reaches every other product selling from the same
//      pricelist, so those products are named. Narrowing it to "just this product"
//      would be a second targeting rule the storefront never reads --
//      Coupon#unavailable_reason asks about the package.
//   2. The coupon's own terms -- amount, window, audience -- are NOT edited here.
//      They belong to the coupon, and the Coupons tab owns them. A second editor for
//      the window would be another place to get it wrong, which is the failure
//      docs/campaign_offer_console.md is written about.
//
// Every date shown is resolved SERVER-side (Shop::WizardPayload#offers_json) against
// today in Pacific, with its zone. No date arithmetic happens in this file.
const BLANK_OFFER = {
  code: '',
  coupon_type: 'PERCENTAGE',
  value: '',
  days_post_job_start: '0',
  days_post_job: '',
  end_date: '',
  use_in_order_flow: false,
};

// The kinds worth offering here. The full list lives on the Coupons tab; these four
// cover a sale run on a pricelist, which is what someone building a product is doing.
const KINDS = [
  ['PERCENTAGE', 'Percentage off'],
  ['PERCENTAGE_WITH_FREE_SHIPPING', 'Percentage off + free shipping'],
  ['FLAT_RATE', 'Dollars off'],
  ['FLAT_RATE_WITH_FREE_SHIPPING', 'Dollars off + free shipping'],
];

const Offers = ({
  product, choices, commit, busy,
}) => {
  const [picked, setPicked] = useState('');
  const [draft, setDraft] = useState(null);
  const offers = choices.offers || { attached: [], attachable: [] };
  const shared = choices.offer_shares_pricelist_with || [];

  const attach = async () => {
    if (!picked) return;
    await commit({ attach_coupon_id: String(picked) });
    setPicked('');
  };

  const field = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  // The pricelist is not asked for: it is the reason to be creating one from here,
  // and attaching it server-side is also what makes an advertised offer scoped by
  // construction (Coupon#broadcast_coupons_must_be_scoped).
  const createOffer = async () => {
    const ok = await commit({ new_offer: draft });
    if (ok) setDraft(null);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {`Discount codes that work on ${product.legacy_package_name || 'this pricelist'}. `}
        Customers type these at checkout, unless the offer is advertised — then every eligible
        shopper sees it and gets it automatically.
      </Typography>

      {shared.length ? (
        <Alert severity="info">
          <strong>An offer here applies to the whole pricelist.</strong>
          {` It will also apply to ${shared.join(', ')}, which sell from the same one.`}
        </Alert>
      ) : null}

      {offers.attached.length ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Takes off</TableCell>
              <TableCell>
                When it runs
                <Typography variant="caption" color="text.secondary" display="block">
                  {`for a school shot ${offers.resolved_for}`}
                </Typography>
              </TableCell>
              <TableCell>Who</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.attached.map((offer) => (
              <TableRow key={offer.id}>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="body2" fontWeight={600}>{offer.code}</Typography>
                    {offer.advertised ? <Chip size="small" color="info" label="advertised" /> : null}
                  </Stack>
                  {/* An offer on several pricelists is not this product's alone to
                      remove, so say how far it reaches before someone detaches it. */}
                  {offer.pricelist_count > 1 ? (
                    <Typography variant="caption" color="text.secondary">
                      {`on ${offer.pricelist_count} pricelists`}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{offer.amount}</TableCell>
                <TableCell>
                  <Typography variant="caption" display="block">
                    {offer.opens_at ? `opens ${offer.opens_at}` : 'no start'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {offer.closes_at ? `through ${offer.closes_at}` : 'no deadline'}
                  </Typography>
                  {offer.customer_told ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {`customer is told ${offer.customer_told}`}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{offer.audience}</Typography>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    color="error"
                    disabled={busy}
                    onClick={() => commit({ detach_coupon_id: String(offer.id) })}
                  >
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No offer runs on this pricelist. That is fine — a product sells at its normal price.
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          select
          label="Add an offer"
          value={picked}
          sx={{ flex: '1 1 380px' }}
          disabled={busy}
          onChange={(e) => setPicked(e.target.value)}
        >
          <MenuItem value="">— pick a code —</MenuItem>
          {offers.attachable.map((offer) => (
            <MenuItem key={offer.id} value={offer.id}>
              {`${offer.code} · ${offer.amount}`}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" sx={{ mt: 0.5 }} disabled={busy || !picked} onClick={attach}>
          Add
        </Button>
      </Stack>

      {draft ? (
        <Box sx={{
          p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1,
        }}
        >
          <Typography variant="subtitle2" gutterBottom>New offer</Typography>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {`It will run on ${product.legacy_package_name || 'this pricelist'} — that part is already
              decided by being here. Everything else about it can be changed on the Coupons tab.`}
          </Typography>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <TextField
              label="Code"
              value={draft.code}
              sx={{ width: 200 }}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              onChange={(e) => field('code', e.target.value)}
            />
            <TextField
              select
              label="What it takes off"
              value={draft.coupon_type}
              sx={{ width: 280 }}
              onChange={(e) => field('coupon_type', e.target.value)}
            >
              {KINDS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField
              label={draft.coupon_type.startsWith('FLAT_RATE') ? 'Dollars' : 'Percent'}
              type="number"
              value={draft.value}
              sx={{ width: 120 }}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={(e) => field('value', e.target.value)}
            />
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            <TextField
              label="Opens (days after picture day)"
              type="number"
              value={draft.days_post_job_start}
              sx={{ width: 220 }}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={(e) => field('days_post_job_start', e.target.value)}
            />
            <TextField
              label="Closes (days after)"
              type="number"
              value={draft.days_post_job}
              sx={{ width: 180 }}
              InputProps={{ inputProps: { min: 0 } }}
              onChange={(e) => field('days_post_job', e.target.value)}
            />
            <TextField
              label="Or a hard cut-off date"
              type="date"
              value={draft.end_date}
              sx={{ width: 220 }}
              InputLabelProps={{ shrink: true }}
              helperText="Stops two days before the date entered"
              onChange={(e) => field('end_date', e.target.value)}
            />
          </Stack>

          <FormControlLabel
            sx={{ display: 'flex', mt: 1 }}
            control={(
              <Checkbox
                checked={!!draft.use_in_order_flow}
                onChange={(e) => field('use_in_order_flow', e.target.checked)}
              />
            )}
            label={(
              <Typography variant="body2">
                Advertise it on the pricelist and apply it automatically
                <Typography component="span" variant="caption" color="text.secondary" display="block">
                  Off means the customer has to type the code. On needs a deadline above.
                </Typography>
              </Typography>
            )}
          />

          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="contained" disabled={busy || !draft.code} onClick={createOffer}>
              Create offer
            </Button>
            <Button disabled={busy} onClick={() => setDraft(null)}>Cancel</Button>
          </Stack>
        </Box>
      ) : (
        <Box>
          <Button size="small" disabled={busy} onClick={() => setDraft(BLANK_OFFER)}>
            + Create a new offer for this pricelist
          </Button>
        </Box>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary">
          The list offers live codes that are not on this pricelist yet. To set who an offer is for,
          which picture days it covers, or how many times it can be used, use the Coupons tab.
        </Typography>
      </Box>
    </Stack>
  );
};

Offers.propTypes = stepPropTypes;
Offers.defaultProps = stepDefaultProps;

export default Offers;
